import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider, useLang } from "@/context/LanguageContext";
import { SyncProvider } from "@/context/SyncContext";
import ConflictDialog from "@/components/ConflictDialog";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Profile from "@/pages/Profile";
import Dashboard from "@/pages/Dashboard";
import Buyers from "@/pages/Buyers";
import Sellers from "@/pages/Sellers";
import Properties from "@/pages/Properties";
import Viewings from "@/pages/Viewings";
import AIGenerator from "@/pages/AIGenerator";
import Templates from "@/pages/Templates";
import Tasks from "@/pages/Tasks";
import Forms from "@/pages/Forms";
import Rooms from "@/pages/Rooms";
import Calendar from "@/pages/Calendar";
import Pipeline from "@/pages/Pipeline";
import Diary from "@/pages/Diary";
import Leads from "@/pages/Leads";
import RoomMeasurements from "@/pages/RoomMeasurements";
import Estimations from "@/pages/Estimations";

const Loader = () => (
  <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
    <span className="font-heading text-2xl text-stone-300 animate-pulse">Julia AI</span>
  </div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

function AppRouter() {
  const location = useLocation();
  // Detect OAuth callback synchronously (during render, before any effect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
      <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
      <Route path="/diary" element={<Protected><Diary /></Protected>} />
      <Route path="/leads" element={<Protected><Leads /></Protected>} />
      <Route path="/rooms" element={<Protected><RoomMeasurements /></Protected>} />
      <Route path="/estimations" element={<Protected><Estimations /></Protected>} />
      <Route path="/buyers" element={<Protected><Buyers /></Protected>} />
      <Route path="/sellers" element={<Protected><Sellers /></Protected>} />
      <Route path="/properties" element={<Protected><Properties /></Protected>} />
      <Route path="/properties/:propertyId/rooms" element={<Protected><Rooms /></Protected>} />
      <Route path="/forms" element={<Protected><Forms /></Protected>} />
      <Route path="/viewings" element={<Protected><Viewings /></Protected>} />
      <Route path="/ai" element={<Protected><AIGenerator /></Protected>} />
      <Route path="/templates" element={<Protected><Templates /></Protected>} />
      <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Read (and clear) the post-update flag once at app load — StrictMode-safe.
let pendingUpdateToast = false;
try {
  pendingUpdateToast = !!sessionStorage.getItem("julia_updated");
  if (pendingUpdateToast) sessionStorage.removeItem("julia_updated");
} catch (err) {
  console.warn("Session storage unavailable — skipping update toast", err);
}

// Shows a one-off toast right after the PWA self-updated to a new deploy.
const UpdateNotice = () => {
  const { t } = useLang();
  useEffect(() => {
    if (!pendingUpdateToast) return;
    pendingUpdateToast = false;
    // Small delay so the Toaster is mounted before the toast fires
    setTimeout(() => toast.success(t("appUpdated")), 800);
  }, [t]);
  return null;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <SyncProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <ConflictDialog />
          <UpdateNotice />
          <Toaster position="bottom-right" richColors />
        </SyncProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
